/**
 * Publieke embeddable lead-capture form pagina (Forms Builder).
 *
 * URL: /forms/{slug}
 *
 * - Haalt formulier-config op via GET /forms-builder/by-slug/{slug}
 * - Rendert dynamische velden op basis van form.fields
 * - Submit via POST /forms-builder/submit/{slug}
 * - Bij success: toon success_message of redirect naar redirect_url
 * - Forced light-mode + Zynq branding (consistent met andere publieke pages)
 */
import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { formsBuilderAPI } from '@/lib/api';
import { CheckCircle2, Send, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';

const LOGO_ICON = 'https://customer-assets.emergentagent.com/job_buyer-match-26/artifacts/52px3160_zynq_logo_icon_e0dde9b0559f42738516da0422c8a0d0.png';

function inputTypeFor(field) {
  switch (field.type) {
    case 'email': return 'email';
    case 'phone':
    case 'tel': return 'tel';
    case 'number': return 'number';
    case 'date': return 'date';
    case 'url': return 'url';
    default: return 'text';
  }
}

function defaultValueFor(field) {
  if (field.type === 'checkbox') return field.default_value === true;
  if (field.type === 'multi_select') return Array.isArray(field.default_value) ? field.default_value : [];
  return field.default_value ?? '';
}

export default function PublicFormPage() {
  const { slug } = useParams();
  const [form, setForm] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');

  // Force light mode (consistent met andere public pages)
  useEffect(() => {
    const root = document.documentElement;
    const hadDark = root.classList.contains('dark');
    root.classList.remove('dark');
    return () => { if (hadDark) root.classList.add('dark'); };
  }, []);

  useEffect(() => {
    formsBuilderAPI.getBySlug(slug)
      .then(res => {
        setForm(res.data);
        const init = {};
        (res.data.fields || []).forEach(f => { init[f.key] = defaultValueFor(f); });
        setValues(init);
      })
      .catch(err => {
        if (err.response?.status === 404) {
          setLoadError('Dit formulier bestaat niet of is niet actief.');
        } else {
          setLoadError('Er ging iets fout bij het laden van het formulier.');
        }
      });
  }, [slug]);

  const accent = form?.accent_color || '#F97316';
  const accentStyle = useMemo(() => ({
    backgroundColor: accent,
    borderColor: accent,
  }), [accent]);

  const updateValue = (key, val) => setValues(p => ({ ...p, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      // Client-side required check als netjes UX
      for (const f of form.fields || []) {
        const v = values[f.key];
        const empty = v === '' || v === null || v === undefined ||
          (Array.isArray(v) && v.length === 0) ||
          (f.type === 'checkbox' && v !== true);
        if (f.required && empty) {
          setError(`Vul '${f.label}' in om door te gaan.`);
          setSubmitting(false);
          return;
        }
      }
      const res = await formsBuilderAPI.submit(slug, values);
      const data = res.data || {};
      if (data.redirect_url) {
        window.location.href = data.redirect_url;
        return;
      }
      setSuccessMessage(data.message || 'Bedankt! We hebben je inzending ontvangen.');
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Er ging iets fout, probeer het straks opnieuw.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loadError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-md w-full border-slate-200">
          <CardContent className="p-8 text-center space-y-3">
            <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto">
              <AlertCircle className="w-6 h-6 text-slate-500" />
            </div>
            <p className="text-slate-700 text-sm">{loadError}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4" data-testid="public-form-success">
        <Card className="max-w-md w-full border-slate-200 shadow-lg">
          <CardContent className="p-8 text-center space-y-4">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto"
              style={{ backgroundColor: `${accent}1A` }}
            >
              <CheckCircle2 className="w-8 h-8" style={{ color: accent }} />
            </div>
            <h2 className="text-xl font-bold text-slate-900">Bedankt!</h2>
            <p className="text-slate-600 text-sm whitespace-pre-line">{successMessage}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4" data-testid="public-form-page">
      <div className="max-w-xl mx-auto">
        <Card className="border-slate-200 shadow-lg overflow-hidden">
          {/* Accent header bar */}
          <div className="h-1.5 w-full" style={{ backgroundColor: accent }} />
          <CardContent className="p-8 space-y-6">
            {form.show_logo !== false && (
              <div className="flex items-center justify-center">
                <img src={LOGO_ICON} alt="Zynq" className="w-10 h-10" />
              </div>
            )}

            <div className="text-center space-y-2">
              <h1 className="text-2xl font-bold tracking-tight text-slate-900" data-testid="form-title">
                {form.name}
              </h1>
              {form.intro_text && (
                <p className="text-slate-600 text-sm whitespace-pre-line">{form.intro_text}</p>
              )}
            </div>

            {error && (
              <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {(form.fields || []).map((field) => (
                <FieldRenderer
                  key={field.key}
                  field={field}
                  value={values[field.key]}
                  onChange={(v) => updateValue(field.key, v)}
                  accent={accent}
                />
              ))}

              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-11 gap-2 text-white hover:opacity-90 transition-opacity"
                style={accentStyle}
                data-testid="public-form-submit"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {submitting ? 'Versturen...' : (form.submit_button_label || 'Verzenden')}
              </Button>
            </form>

            <p className="text-[10px] text-slate-400 text-center pt-2">
              Powered by Zynq · Vitaliteit als missie
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function FieldRenderer({ field, value, onChange, accent }) {
  const labelEl = (
    <Label className="text-sm font-medium text-slate-800">
      {field.label} {field.required && <span style={{ color: accent }}>*</span>}
    </Label>
  );

  const help = field.help_text ? (
    <p className="text-[11px] text-slate-500 mt-1">{field.help_text}</p>
  ) : null;

  if (field.type === 'textarea') {
    return (
      <div className="space-y-1.5">
        {labelEl}
        <Textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder || ''}
          rows={4}
          data-testid={`field-${field.key}`}
        />
        {help}
      </div>
    );
  }

  if (field.type === 'select') {
    return (
      <div className="space-y-1.5">
        {labelEl}
        <select
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`field-${field.key}`}
          className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2"
          style={{ outlineColor: accent }}
        >
          <option value="">{field.placeholder || `Selecteer ${field.label.toLowerCase()}...`}</option>
          {(field.options || []).map((opt, i) => (
            <option key={i} value={opt.value || opt.label}>{opt.label}</option>
          ))}
        </select>
        {help}
      </div>
    );
  }

  if (field.type === 'multi_select') {
    const current = Array.isArray(value) ? value : [];
    return (
      <div className="space-y-1.5">
        {labelEl}
        <div className="flex flex-wrap gap-2">
          {(field.options || []).map((opt, i) => {
            const v = opt.value || opt.label;
            const active = current.includes(v);
            return (
              <button
                type="button"
                key={i}
                onClick={() => {
                  if (active) onChange(current.filter(x => x !== v));
                  else onChange([...current, v]);
                }}
                className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                  active ? 'text-white' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                }`}
                style={active ? { backgroundColor: accent, borderColor: accent } : undefined}
                data-testid={`field-${field.key}-${i}`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        {help}
      </div>
    );
  }

  if (field.type === 'radio') {
    return (
      <div className="space-y-1.5">
        {labelEl}
        <div className="space-y-1">
          {(field.options || []).map((opt, i) => {
            const v = opt.value || opt.label;
            return (
              <label key={i} className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
                <input
                  type="radio"
                  name={field.key}
                  checked={value === v}
                  onChange={() => onChange(v)}
                  style={{ accentColor: accent }}
                  data-testid={`field-${field.key}-${i}`}
                />
                {opt.label}
              </label>
            );
          })}
        </div>
        {help}
      </div>
    );
  }

  if (field.type === 'checkbox') {
    return (
      <div className="space-y-1.5">
        <label className="flex items-start gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={value === true}
            onChange={(e) => onChange(e.target.checked)}
            className="mt-0.5"
            style={{ accentColor: accent }}
            data-testid={`field-${field.key}`}
          />
          <span className="text-sm text-slate-700">
            {field.label} {field.required && <span style={{ color: accent }}>*</span>}
          </span>
        </label>
        {help}
      </div>
    );
  }

  // text / email / phone / tel / number / date / url
  return (
    <div className="space-y-1.5">
      {labelEl}
      <Input
        type={inputTypeFor(field)}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder || ''}
        data-testid={`field-${field.key}`}
      />
      {help}
    </div>
  );
}
